
export interface ScreenSizes {
    minScreenWidth: number;
    mediaQuery: string;
    sizesSupported: googletag.SingleSize[];
    labels: string[];
}

export declare type SizeMapping = ScreenSizes[];

export const billboard: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [
            [980, 250], [980, 120], [980, 90],
            [970, 250], [970, 90],
            [960, 90],
            [950, 90],
            [930, 180],
            [800, 250], [800, 90],
            [768, 90],
            [750, 200], [750, 100],
            [728, 90],
        ],
        labels: [ 'billboard-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'billboard-mobile' ],
    },
];

export const text: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [
            [480, 300],
        ],
        labels: [ 'text-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'text-mobile' ],
    },
];

export const rectangleDesktopOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[336, 280], [300, 250], [250, 250], 'fluid'],
        labels: [ 'rectangle-desktop-only-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'rectangle-desktop-only-mobile' ],
    },
];

export const rectangleMobileOnly: SizeMapping = [
    {
        minScreenWidth: 600, // desktop (not shown)
        mediaQuery: '(min-width: 600px)',
        sizesSupported: [],
        labels: [ 'rectangle-mobile-only-desktop' ],
    },
    {
        minScreenWidth: 360,  // mobile (nexus5 - 360px)
        mediaQuery: '(min-width: 360px) and (max-width: 599px)',
        sizesSupported: [[336, 280], [300, 250], [250, 250], 'fluid'],
        labels: [ 'rectangle-mobile-only-nexus5' ],
    },
    {
        minScreenWidth: 0,  // mobile (iphone4 - 320px)
        mediaQuery: '(max-width: 359px)',
        sizesSupported: [[300, 250], [250, 250], 'fluid'],
        labels: [ 'rectangle-mobile-only-iphone4' ],
    },
];

export const halfpage: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[300, 600], [160, 600], [120, 600]],
        labels: [ 'halfpage-desktop' ],
    },
    {
        minScreenWidth: 0,  // mobile (not shown)
        mediaQuery: '(max-width: 979px)', // mobile (not shown)
        sizesSupported: [],
        labels: [ 'halfpage-mobile' ],
    },
];

export const halfpageMobileOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [],
        labels: [ 'halfpage-mobile-only-desktop' ],
    },
    {
        minScreenWidth: 0,  // mobile (not shown)
        mediaQuery: '(max-width: 979px)', // mobile (not shown)
        sizesSupported: [[300, 600], [160, 600], [120, 600]],
        labels: [ 'halfpage-mobile-only-mobile' ],
    },
];

export const halfpageOffsite: SizeMapping = [
    {
        minScreenWidth: 1590, // desktop (1590 - 980 stranka - 10 margin) / 2 >= 300
        mediaQuery: '(min-width: 1590px)',
        sizesSupported: [[300, 600], [160, 600], [120, 600]],
        labels: [ 'halfpage-offsite-1590' ],
    },
    {
        minScreenWidth: 1310, // desktop  (1310 - 980 stranka - 10 margin) / 2 >= 160
        mediaQuery: '(min-width: 1310px) and (max-width: 1589px)',
        sizesSupported: [[160, 600], [120, 600]],
        labels: [ 'halfpage-offsite-1310' ],
    },
    {
        minScreenWidth: 1230, // desktop  (1230 - 980 stranka - 10 margin) / 2 >= 120
        mediaQuery: '(min-width: 1230px) and (max-width: 1319px)',
        sizesSupported: [[120, 600]],
        labels: [ 'halfpage-offsite-1230' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 1229px)',
        sizesSupported: [],
        labels: [ 'halfpage-offsite-mobile' ],
    },
];

export const brandingDesktopOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[2000, 1400], [970, 250]],
        labels: [ 'branding-desktop-only-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'branding-desktop-only-mobile' ],
    },
];

export const brandingMobileOnly: SizeMapping = [
    {
        minScreenWidth: 600, // desktop (not shown)
        mediaQuery: '(min-width: 600px)',
        sizesSupported: [],
        labels: [ 'branding-mobile-only-desktop' ],
    },
    {
        // Creative is wider than screen width. When displaying, we center it
        // inside slot and hide parts that overflow
        minScreenWidth: 0,  // mobile (iphone4 - 320px; up to iphone 6+ - 416px)
        mediaQuery: '(max-width: 599px)',
        sizesSupported: [[420, 100], [320, 100]],
        labels: [ 'branding-mobile-only-mobile' ],
    },
];

export const mail: SizeMapping = [
    {
        minScreenWidth: 0,
        mediaQuery: '(min-width: 0px)',
        sizesSupported: [[730, 3000], 'fluid'],
        labels: [ 'mail' ],
    },
];

export const mailCtaButton = [
    {
        minScreenWidth: 0,
        mediaQuery: '(min-width: 0px)',
        sizesSupported: ['fluid'],
        labels: [ 'mailCtaButton' ],
    },
];

export default [
    ...billboard,
    ...text,
    ...rectangleDesktopOnly,
    ...rectangleMobileOnly,
    ...halfpage,
    ...halfpageOffsite,
    ...brandingDesktopOnly,
    ...brandingMobileOnly,
    ...mail,
];
